// src/shared-components/forms/add-artwork/shipping/index.jsx
import { useState } from 'react';

import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import XIcon        from '@/shared-components/icons/components/public/XIcon';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text         from '@/shared-components/text/components/Text';

import AddException        from './components/AddException';
import SaveShippingData    from './components/SaveShippingData';
import ShippingCard        from './components/ShippingCard';
import SelectShippingData  from './select-shipping-data';

import './index.css';

/**
 * Shipping step
 * @param {object}   formState
 * @param {function} setFormState
 * @param {boolean}  previewMode       → readonly (public preview)
 * @param {boolean}  editMode          → edición de artwork existente
 * @param {object}   permissions       → claves de autorización (ej: { shippingTariffs: true/false })
 * @param {array}    formError / setFormError
 */
export default function Shipping({
  formState,
  setFormState,
  previewMode,
  formError,
  setFormError,
  editMode = false,
  permissions = {},
  galleryPermissionCheck = null,
}) {
  const t = useTranslations('common.forms.add_artwork');
  const { state } = useStateHandler();
  const userType = state?.user_session?.user_type;
  const isArtistAccount = userType === 'artist' || userType === 'gallery_artist';
  const shippingState = formState?.artwork_shipping ?? {};
  const shippingExceptions = shippingState.artwork_shipping_exceptions ?? [];
  /* ────────────── PERMISOS ───────────────────────────────────────── */
  // Si estamos en edición y se pasa objeto de permisos:
  const hasArtistPerms = editMode && Object.keys(permissions).length > 0;
  const isGalleryMember = typeof galleryPermissionCheck === 'function';
  const canGallery = key => (!isGalleryMember ? true : galleryPermissionCheck(key));
  // Con uno solo basta (todo el bloque de shipping)
  const canEditBase = hasArtistPerms
    ? !!permissions.shippingTariffs
    : canGallery('artworks.edit_shipping');
  // Artists may edit shipping only when the gallery granted this explicit
  // field-level permission. Artist uploads remain unchanged: they do not pass
  // `hasArtistPerms` because this policy is only active in edit mode.
  const canEdit = hasArtistPerms
    ? Boolean(permissions.shippingTariffs)
    : !isArtistAccount && canEditBase;

  /* ────────────── POP-UP campo bloqueado ─────────────────────────── */
  const [showDeniedPopup, setShowDeniedPopup] = useState(false);
  const [showActionRejectedPopup, setShowActionRejectedPopup] = useState(false);
  const triggerDeniedPopup = () => {
    if (isGalleryMember) {
      setShowActionRejectedPopup(true);
      return;
    }
    setShowDeniedPopup(true);
  };

  // envuelve children y, si está disabled, intercepta el click
  const wrapDisabled = (disabled, children) => (
    <div
      style={{ display: 'contents' }}
      onClick={e => {
        if (disabled) {
          e.preventDefault();
          triggerDeniedPopup();
        }
      }}
    >
      {children}
    </div>
  );

  /* ────────────── Handlers de cambio ─────────────────────────────── */
  function onInputShippingData(key, value) {
    setFormState(prev => ({
      ...prev,
      artwork_shipping: { ...prev.artwork_shipping, [key]: value },
    }));

    setFormError(prevErrors =>
      prevErrors ? prevErrors.filter(err => err.element !== key) : []
    );
  }

  /* ────────────── RENDER ─────────────────────────────────────────── */
  return (
    <>
      <div
        className={`add-artwork-shipping-form ${
          previewMode || !canEdit ? 'read-only' : ''
        }`}
      >
        {/* Selector de tarifas guardadas (solo si se puede editar) */}
        {!previewMode && canEdit && (
          <SelectShippingData setFormState={setFormState} />
        )}

        {/* OWN – galería/autor paga */}
        {wrapDisabled(
          !canEdit,
          <ShippingCard
            location={shippingState.artwork_shipping_own?.location}
            element="artwork_shipping_own"
            formError={formError}
            previewMode={previewMode || !canEdit}
            value={shippingState.artwork_shipping_own}
            currency={formState?.artwork_about?.artwork_currency}
            onChange={v => onInputShippingData('artwork_shipping_own', v)}
            disabled={!canEdit}
          />
        )}

        {/* REST OF WORLD */}
        {wrapDisabled(
          !canEdit,
          <ShippingCard
            location={t('shipping.rest_of_world')}
            element="artwork_shipping_rest"
            formError={formError}
            previewMode={previewMode || !canEdit}
            value={shippingState.artwork_shipping_rest}
            currency={formState?.artwork_about?.artwork_currency}
            onChange={v => onInputShippingData('artwork_shipping_rest', v)}
            disabled={!canEdit}
          />
        )}

        {/* EXCEPCIONES */}
        {shippingExceptions.map(exc =>
          wrapDisabled(
            !canEdit,
            <ShippingCard
              key={exc.id}
              location={exc.location}
              element={exc.element}
              formError={formError}
              previewMode={previewMode}
              disabled={!canEdit}
              value={exc}
              currency={formState?.artwork_about?.artwork_currency}
              onChange={v =>
                onInputShippingData(
                  'artwork_shipping_exceptions',
                  shippingExceptions.map(i =>
                    i.id === exc.id ? v : i
                  )
                )
              }
              deleteException={() =>
                onInputShippingData(
                  'artwork_shipping_exceptions',
                  shippingExceptions.filter(
                    i => i.id !== exc.id
                  )
                )
              }
            />
          )
        )}

        {/* Botones “Añadir excepción” y “Guardar” (solo si editable) */}
        {!previewMode && canEdit && (
          <>
            <AddException setFormState={setFormState} type="COUNTRY_EXCEPTION" />
            <AddException setFormState={setFormState} type="CONTINENT_EXCEPTION" />
            <SaveShippingData formState={formState} />
          </>
        )}
      </div>

      {/* Pop-up de permisos denegados */}
      <GenericPopup
        open={showDeniedPopup}
        onClick={() => setShowDeniedPopup(false)}
        opacity
        className="generic-popup_content"
      >
        <div className="generic-popup__header">
          <XIcon
            className="generic-popup__close"
            onClick={() => setShowDeniedPopup(false)}
          />
          <Text className="generic-popup__header-text-theme">
            {t('shipping.field_locked_title')}
          </Text>
        </div>
        <p>{t('shipping.field_locked_description')}</p>
      </GenericPopup>
      <ActionRejectedPopup
        open={showActionRejectedPopup}
        close={() => setShowActionRejectedPopup(false)}
        description={t('shipping.permission_denied')}
      />
    </>
  );
}
